import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { Navbar } from '../components';
import { Footer } from '../containers';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebaseconfig';
import SEOMeta from '../components/SEOMeta/SEOMeta';
import { PROBLEMS, TOTAL_PROBLEM_COUNT } from '../data/dsaProblemCatalog';
import './ProblemsPage.css';

const PROGRESS_STORAGE_KEY = 'algodoor_dsa_progress';

const readLocalProgress = (userKey) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(PROGRESS_STORAGE_KEY) || '{}');
    return parsed[userKey] || { solved: [] };
  } catch (_) {
    return { solved: [] };
  }
};

const formatPatternLabel = (value) => (value === 'Trie' ? 'Tree (Trie)' : value);

const ProblemsPage = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(() => searchParams.get('q') || '');
  const [company, setCompany] = useState(() => searchParams.get('company') || 'All');
  const [pattern, setPattern] = useState(() => searchParams.get('pattern') || 'All');
  const [difficulty, setDifficulty] = useState(() => searchParams.get('difficulty') || 'All');
  const [source, setSource] = useState(() => searchParams.get('source') || 'All');
  const [progress, setProgress] = useState({ solved: [] });

  const userProgressKey = currentUser ? `user_${currentUser.id}` : 'guest';

  useEffect(() => {
    const loadProgress = async () => {
      const local = readLocalProgress(userProgressKey);
      setProgress(local);
      if (!currentUser) return;

      try {
        const snap = await getDoc(doc(db, 'dsaProgress', userProgressKey));
        if (snap.exists()) setProgress({ solved: snap.data().solved || [] });
      } catch (_) {
        // silent fallback to local
      }
    };

    loadProgress();
  }, [currentUser, userProgressKey]);

  const companies = useMemo(() => ['All', ...Array.from(new Set(PROBLEMS.flatMap((p) => p.companies))).sort()], []);
  const patternOptions = useMemo(() => {
    const allPatterns = Array.from(new Set(PROBLEMS.flatMap((p) => p.patterns))).sort();
    return ['All', ...allPatterns];
  }, []);

  const filtered = useMemo(() => PROBLEMS.filter((problem) => {
    if (source !== 'All' && problem.source !== source) return false;
    if (company !== 'All' && !problem.companies.includes(company)) return false;
    if (pattern !== 'All' && !problem.patterns.includes(pattern)) return false;
    if (difficulty !== 'All' && problem.difficulty !== difficulty) return false;
    const haystack = `${problem.title} ${problem.patterns.join(' ')} ${problem.companies.join(' ')}`.toLowerCase();
    return haystack.includes(query.toLowerCase());
  }), [source, company, pattern, difficulty, query]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (query) next.set('q', query);
    if (company !== 'All') next.set('company', company);
    if (pattern !== 'All') next.set('pattern', pattern);
    if (difficulty !== 'All') next.set('difficulty', difficulty);
    if (source !== 'All') next.set('source', source);
    setSearchParams(next, { replace: true });
  }, [query, company, pattern, difficulty, source, setSearchParams]);

  return (
    <div className="problems-page">
      <SEOMeta
        title="All DSA Problems | Algodoor"
        description="Explore DSA problems by company, pattern, and difficulty."
        url="/problems"
        type="WebPage"
      />

      <div className="gradient__bg">
        <Navbar />
      </div>

      <section className="problems-hero">
        <h1>Problem Library</h1>
        <p>Filter by companies, algorithmic patterns, and difficulty. Open any problem to solve and track status.</p>
        <p>Loaded Problems: {TOTAL_PROBLEM_COUNT}. All problems are listed at once below.</p>
      </section>

      <section className="problems-filters">
        <input
          type="text"
          placeholder="Search questions..."
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />

        <select value={company} onChange={(event) => setCompany(event.target.value)}>
          {companies.map((item) => <option key={item} value={item}>{item}</option>)}
        </select>

        <select value={pattern} onChange={(event) => setPattern(event.target.value)}>
          {patternOptions.map((item) => <option key={item} value={item}>{formatPatternLabel(item)}</option>)}
        </select>

        <select value={source} onChange={(event) => setSource(event.target.value)}>
          <option value="All">Total</option>
          <option value="curated">Curated</option>
          <option value="online">Online</option>
          <option value="docx-batch">Docs</option>
        </select>

        <select value={difficulty} onChange={(event) => setDifficulty(event.target.value)}>
          <option value="All">All Difficulty</option>
          <option value="Easy">Easy</option>
          <option value="Medium">Medium</option>
          <option value="Hard">Hard</option>
        </select>
      </section>

      <section className="problems-table-wrap">
        <table className="problems-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Title</th>
              <th>Difficulty</th>
              <th>Patterns</th>
              <th>Companies</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((problem) => (
              <tr key={problem.id}>
                <td className="status-cell">
                  {progress.solved.includes(problem.id) ? <span className="status-done">✓</span> : ''}
                </td>
                <td>
                  <Link
                    to={`/problem/${problem.id}`}
                    state={{ fromSearch: location.search }}
                    className="problem-link"
                  >
                    {problem.title}
                  </Link>
                </td>
                <td>
                  <span className={`difficulty ${problem.difficulty.toLowerCase()}`}>{problem.difficulty}</span>
                </td>
                <td>{problem.patterns.slice(0, 3).map(formatPatternLabel).join(', ')}</td>
                <td>{problem.companies.join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <Footer />
    </div>
  );
};

export default ProblemsPage;
